import { Vector3 } from "@babylonjs/core";
import { Tools } from "@babylonjs/core/Misc/tools";
import { CutoutType } from "./cutout";
import Joint, { ConnectorType, CleatLocation, DuctType } from "./joint";
import MachineSettings, { LockSeamSetting, NotchDepthSetting } from "./machine-settings";
export default class JointRenderSpecs {
    private joint: Joint;
    private lockSeamLengths: LockSeamSetting;

    /** Connector 1 depths */
    c1Depths: NotchDepthSetting;
    /** Connector 2 depths */
    c2Depths: NotchDepthSetting;

    get ductType(): DuctType {
        return this.joint.ductType;
    }

    /**
        This camera distance shows the full part
    */
    get defaultCameraDistance(): number {
        const halfLength = this.lengthOddLegs + this.lengthEvenLegs;
        let cameraDistance = Math.sqrt(halfLength * this.coilWidth / 2) * 3.85;
        return cameraDistance;
    }

    get jointBlankMeshSpecs(): JointBlankMeshSpecs {
        return {
            name: "blank",
            height: this.coilThickness,
            width: this.coilWidth,
            depth: this.stretchOut,
            position: new Vector3(0, this.coilThickness / 2, 0),
        }
    }

    get cutouts() { return this.joint.cutouts };

    get cutoutMeshSpecs(): CutoutMeshSpecs[] {
        return this.joint.cutouts.map(c => {
            let cutoutPosition = new Vector3(
                -this.coilWidth / 2 + c.distanceFromC1,
                this.coilThickness / 2,
                this.stretchOut / 2 - this.maleLength - c.distanceFromMale
            );

            let cutoutMeshSpecs: CutoutMeshSpecs;

            switch (c.type) {
                case CutoutType.Rectangle:
                    cutoutMeshSpecs = {
                        type: c.type,
                        name: "cutout-rect",
                        position: cutoutPosition,
                        height: this.coilThickness,
                        width: c.width,
                        depth: c.length
                    };
                    break;
                case CutoutType.Round:
                    if (c.width !== c.length) throw new Error('Invalid round cutout parameters');

                    cutoutMeshSpecs = {
                        type: c.type,
                        name: "cutout-round",
                        position: cutoutPosition,
                        height: this.coilThickness,
                        diameterTop: c.width,
                        diameterBottom: c.width
                    };
                    break;
                default:
                    throw new Error('Unexpected cutout type');
            }

            return cutoutMeshSpecs;
        });
    };

    constructor(joint: Joint, machineSettings: MachineSettings) {
        this.joint = joint;

        this.c1Depths = machineSettings.notchDepthSettings.get(joint.connector1);
        this.c2Depths = machineSettings.notchDepthSettings.get(joint.connector2);
        this.lockSeamLengths = machineSettings.lockSeamSettings.get(joint.lockSeam);
    }

    get c1Type() { return this.joint.connector1 }
    get c2Type() { return this.joint.connector2 }

    get c1TypeLabel() { return this.joint.c1TypeLabel }
    get c2TypeLabel() { return this.joint.c2TypeLabel }

    get c1FormedMeshSpecs(): ConnectorMeshSpecs {
        return {
            position: new Vector3(
                this.coilWidth / -2 + this.c1Depths.veeDepth,
                this.coilThickness / 2,
                this.stretchOut / -2 + this.femaleLength
            ),
            rotationY: 0,
            connectorType: this.c1Type,
        };
    }

    get c2FormedMeshSpecs(): ConnectorMeshSpecs {
        return {
            position: new Vector3(
                this.coilWidth / 2 - this.c2Depths.veeDepth,
                this.coilThickness / 2,
                this.stretchOut / 2 - this.maleLength
            ),
            rotationY: Tools.ToRadians(180),
            connectorType: this.c2Type,
        };
    }

    get lockSeamTypeLabel() { return this.joint.lockSeamTypeLabel }

    get lengthOddLegs() { return this.joint.lengthOddLegs }
    get lengthEvenLegs() { return this.joint.lengthEvenLegs }

    get maleLength() { return this.lockSeamLengths.maleLength; }
    get femaleLength() { return this.lockSeamLengths.femaleLength; }

    get coilWidth() { return this.joint.coilWidth; }

    get coilThickness() {
        //Todo: lookup thickness for this.joint.coilGauge number
        /*
            For now we return 18ga thickness because of hard coded flange profile limitations in our rendering code
            See issues #49 and #50
        */
        return 0.0478;
    }

    get stretchOut() {
        return this.maleLength +
            this.totalLegLengths +
            this.femaleLength;
    }

    get totalLegLengths() {
        return this.joint.lengthOddLegs * 2 + this.joint.lengthEvenLegs * 2;
    }

    get bends(): number[] {
        const firstVee = this.stretchOut / 2 - this.maleLength - this.joint.lengthOddLegs;

        return [
            firstVee,
            firstVee - this.joint.lengthEvenLegs,
            firstVee - this.joint.lengthEvenLegs - this.joint.lengthOddLegs,
        ];
    }

    get animationSpecs(): AnimationSpecs {
        return {
            maleBendAngle: Tools.ToRadians(90),
            maleBendTime: 1,
            legBendAngle: Tools.ToRadians(89),
            legBendTime: 2,
            jointFlipAngle: Tools.ToRadians(180),
            jointFlipTime: 3,
        };
    }

    get nodeAnimationSpecs(): NodeAnimationSpecs[] {
        let specs = this.animationSpecs;
        return [
            { name: 'joint Flip', targetProperty: 'rotation.z', targetValue: specs.jointFlipAngle, animationTime: specs.jointFlipTime, nodeName: 'leg 4 seam node' },
            { name: 'male Bend', targetProperty: 'rotation.x', targetValue: specs.maleBendAngle, animationTime: specs.maleBendTime, nodeName: 'male bend node' },
            { name: 'leg 1 Bend', targetProperty: 'rotation.x', targetValue: specs.legBendAngle, animationTime: specs.legBendTime, nodeName: 'leg 1 bend node' },
            { name: 'leg 2 Bend', targetProperty: 'rotation.x', targetValue: specs.legBendAngle, animationTime: specs.legBendTime, nodeName: 'leg 2 bend node' },
            { name: 'leg 3 Bend', targetProperty: 'rotation.x', targetValue: specs.legBendAngle, animationTime: specs.legBendTime, nodeName: 'leg 3 bend node' },
        ]
    }

    get veeNotchJointConnectorMeshSpecs(): { c1Position: Vector3, c2Position: Vector3, rotationX: number }[] {
        return this.bends.map(bend => {
            return {
                c1Position: new Vector3(this.coilWidth / -2 + this.c1Depths.veeDepth + 0.25, -0.25, bend),
                c2Position: new Vector3(this.coilWidth / 2 - this.c2Depths.veeDepth - 0.25, 0, bend),
                rotationX: Tools.ToRadians(-90),
            };
        });
    }

    getJointSectionSpecs(c1FlatWidth: number, c2FlatWidth: number): JointSectionSpecs {
        const height = 5;

        const getMeshSpecsPositionedOnZAtHalfDepth = (height: number, width: number, depth: number): MeshSpecs => {
            return {
                    height,
                    width,
                depth,
                position: new Vector3(0, 0, depth / 2),
            }
        }

        const getSectionMeshSpecsForC1 = (height: number, legLength: number): MeshSpecs => {
            return {
                    height,
                    width: this.c1Depths.veeDepth,
                depth: legLength,
                position: new Vector3(this.coilWidth / -2 + this.c1Depths.veeDepth / 2, 0, legLength / 2),
            }
        }

        const getSectionMeshSpecsForC2 = (height: number, legLength: number): MeshSpecs => {
            return {
                    height,
                    width: this.c1Depths.veeDepth,
                depth: legLength,
                position: new Vector3(this.coilWidth / 2 + this.c2Depths.veeDepth / -2, 0, legLength / 2),
            }
        }

        const getMidSectionMeshSpecsForLeg = (c1FlatWidth: number, c2FlatWidth: number, height: number, legLength: number): MeshSpecs => {
            const width = this.coilWidth - c1FlatWidth - c2FlatWidth;

            let position = new Vector3();
            position.x = (c1FlatWidth - c2FlatWidth) / 2;

            return {
                    height,
                    width,
                depth: legLength,
                position: position.add(new Vector3(0, 0, legLength / 2)),
            }
        }

        return {
            maleSection: getMeshSpecsPositionedOnZAtHalfDepth(height, this.coilWidth, this.maleLength),

            oddLegSpecsMid: getMidSectionMeshSpecsForLeg(c1FlatWidth, c2FlatWidth, height, this.lengthOddLegs),
            oddLegSpecsC1: getSectionMeshSpecsForC1(height, this.lengthOddLegs),
            oddLegSpecsC2: getSectionMeshSpecsForC2(height, this.lengthOddLegs),
            oddLegSpecsFull: getMeshSpecsPositionedOnZAtHalfDepth(height, this.coilWidth, this.lengthOddLegs),

            evenLegSpecsMid: getMidSectionMeshSpecsForLeg(c1FlatWidth, c2FlatWidth, height, this.lengthEvenLegs),
            evenLegSpecsC1: getSectionMeshSpecsForC1(height, this.lengthEvenLegs),
            evenLegSpecsC2: getSectionMeshSpecsForC2(height, this.lengthEvenLegs),
            evenLegSpecsFull: getMeshSpecsPositionedOnZAtHalfDepth(height, this.coilWidth, this.lengthEvenLegs),

            femaleSection: getMeshSpecsPositionedOnZAtHalfDepth(height, this.coilWidth, this.femaleLength),
        };
    }

    shouldFormC1Odds(): boolean {
        return this.shouldFormConnectorOnOdds(this.joint.connector1);
    }

    shouldFormC1Evens(): boolean {
        return this.shouldFormConnectorOnEvens(this.joint.connector1);
    }

    shouldFormC2Odds(): boolean {
        return this.shouldFormConnectorOnOdds(this.joint.connector2);
    }

    shouldFormC2Evens(): boolean {
        return this.shouldFormConnectorOnEvens(this.joint.connector2);
    }

    shouldFormConnectorOnOdds(connectorType: ConnectorType): boolean {
        switch (connectorType) {
            case ConnectorType.SandD: // S&D
                return this.joint.cleatLocation === CleatLocation.DriveOnOddLegs || this.joint.cleatLocation === CleatLocation.DriveOnAllLegs;
            default:
                return true;
        }
    }

    shouldFormConnectorOnEvens(connectorType: ConnectorType): boolean {
        switch (connectorType) {
            case ConnectorType.SandD: // S&D
                return this.joint.cleatLocation === CleatLocation.DriveOnEvenLegs || this.joint.cleatLocation === CleatLocation.DriveOnAllLegs;
            default:
                return true;
        }
    }
}

export type JointBlankMeshSpecs = {
    name: string,
    height: number,
    width: number,
    depth: number,
    position: Vector3
}

export type CutoutMeshSpecs =
    { type: CutoutType, name: string, position: Vector3, height: number } &
    ({ width: number, depth: number } | { diameterTop: number, diameterBottom: number });

export type ConnectorMeshSpecs = { position: Vector3, rotationY: number, connectorType: ConnectorType }

export type JointSectionSpecs = {
    maleSection: MeshSpecs,
    oddLegSpecsMid: MeshSpecs;
    oddLegSpecsC1: MeshSpecs;
    oddLegSpecsC2: MeshSpecs;
    oddLegSpecsFull: MeshSpecs;
    evenLegSpecsMid: MeshSpecs;
    evenLegSpecsC1: MeshSpecs;
    evenLegSpecsC2: MeshSpecs;
    evenLegSpecsFull: MeshSpecs;
    femaleSection: MeshSpecs;
}

export type MeshSpecs = {
    height: number;
    width: number;
    depth: number;
    position: Vector3;
}

export interface AnimationSpecs {
    //* Radians */
    maleBendAngle: number;
    //* Seconds */
    maleBendTime: number;
    //* Radians */
    legBendAngle: number;
    //* Seconds */
    legBendTime: number;
    //* Radians */
    jointFlipAngle: number;
    //* Seconds */
    jointFlipTime: number;
}

export interface NodeAnimationSpecs {
    name: string;
    targetProperty: string;
    targetValue: number;
    animationTime: number;
    nodeName: string;
}
